// src/hooks/useScheduleLogic.js
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { exportToExcel } from "../utils/excelExport";
import { autocompleteShifts } from "../utils/autocompleteShifts.js";

const useScheduleLogic = (defaults, onSaveSchedule, userSchedules = []) => {
  const { t } = useTranslation();
  const editingSchedule = defaults.schedule;
  const isEditing = !!editingSchedule;
  const people = editingSchedule?.numPeople || defaults.numPeople;
  const days = editingSchedule?.numDays || defaults.numDays;
  const maxShiftsPerDay =
    editingSchedule?.maxShiftsPerDay || defaults?.maxShiftsPerDay || 1;
  const maxShiftsPerPerson =
    editingSchedule?.maxShiftsPerPerson || defaults?.maxShiftsPerPerson || 3;
  const minEmptyDaysBetweenShifts =
    editingSchedule?.minEmptyDaysBetweenShifts ||
    defaults?.minEmptyDaysBetweenShifts ||
    1;
  const shifts = editingSchedule?.shifts || defaults?.shifts;
  // Initialize participants from existing schedule or create new ones
  const [participants, setParticipants] = useState(() => {
    if (editingSchedule && editingSchedule.participants) {
      // Load existing participants
      return editingSchedule.participants;
    } else {
      // Create new participants (original logic)
      return Array.from({ length: people }, (_, i) => ({
        name: `${t("Person")} ${i + 1}`,
        maxShiftsPerPerson: maxShiftsPerPerson,
        minEmptyDaysBetweenShifts: minEmptyDaysBetweenShifts,
      }));
    }
  });

  // Initialize schedule from existing data or create new one
  const [schedule, setSchedule] = useState(() => {
    if (editingSchedule && editingSchedule.schedule) {
      // Load existing schedule
      return editingSchedule.schedule;
    } else {
      // Create new empty schedule (original logic)
      const initial = {};
      participants.forEach((_, pIndex) => {
        initial[pIndex] = {};
        for (let d = 0; d < days; d++) {
          initial[pIndex][d] = ""; // no shift assigned
        }
      });
      return initial;
    }
  });

  // Initialize holidays from existing data or create new array
  const [holidays, setHolidays] = useState(() => {
    if (editingSchedule && editingSchedule.holidays) {
      return editingSchedule.holidays;
    } else {
      return [];
    }
  });
  const [isLeftClickDragging, setIsLeftClickDragging] = useState(false);
  const [dragRowIndex, setDragRowIndex] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editData, setEditData] = useState({
    name: "",
    maxShiftsPerPerson: 0,
    minEmptyDaysBetweenShifts: 0,
  });

  // All your handler functions will go here
  const handleAutocomplete = () => {
    setSchedule((prev) => {
      return autocompleteShifts(prev, participants, shifts, days, holidays);
    });
  };

  const handleClear = () => {
    setSchedule((prev) => {
      const newSchedule = {};
      Object.keys(prev).forEach((pIndex) => {
        newSchedule[pIndex] = {};
        Object.keys(prev[pIndex]).forEach((dayIndex) => {
          // Clear only assignments, keep "Unavailable" and holidays intact
          if (prev[pIndex][dayIndex] !== "" && prev[pIndex][dayIndex] !== "-") {
            newSchedule[pIndex][dayIndex] = "";
          } else {
            newSchedule[pIndex][dayIndex] = prev[pIndex][dayIndex];
          }
        });
      });
      return newSchedule;
    });
  };

  const handleClearAll = () => {
    // Clear entire schedule including assignments and unavailables
    setSchedule((prev) => {
      const newSchedule = {};
      Object.keys(prev).forEach((pIndex) => {
        newSchedule[pIndex] = {};
        Object.keys(prev[pIndex]).forEach((dayIndex) => {
          newSchedule[pIndex][dayIndex] = "";
        });
      });
      // Clear holidays as well if stored in state
      setHolidays([]);
      return newSchedule;
    });
  };

  const toggleUnavailableCell = (pIndex, dayIndex) => {
    setSchedule((prev) => {
      const newSchedule = { ...prev };
      newSchedule[pIndex] = { ...newSchedule[pIndex] };

      const current = newSchedule[pIndex][dayIndex];
      if (current === "-") {
        newSchedule[pIndex][dayIndex] = "";
      } else {
        if (current === "") {
          newSchedule[pIndex][dayIndex] = "-";
        }
      }
      return newSchedule;
    });
  };

  const handleMouseEnterLeft = (pIndex, dayIndex) => {
    if (isLeftClickDragging && dragRowIndex === pIndex) {
      toggleUnavailableCell(pIndex, dayIndex);
    }
  };

  const handleMouseDownLeft = (e, pIndex, dayIndex) => {
    if (e.button === 0) {
      e.preventDefault();
      setIsLeftClickDragging(true);
      setDragRowIndex(pIndex);
      toggleUnavailableCell(pIndex, dayIndex);
    }
  };

  const handleMouseUpLeft = (e) => {
    if (e.button === 0 && isLeftClickDragging) {
      e.preventDefault();
      setIsLeftClickDragging(false);
      setDragRowIndex(null);
    }
  };

  const handleCellRightClick = (e, pIndex, dayIndex) => {
    e.preventDefault();

    setSchedule((prev) => {
      const newSchedule = { ...prev };
      newSchedule[pIndex] = { ...newSchedule[pIndex] };

      const current = newSchedule[pIndex][dayIndex];

      // Find index of current shift in shifts array by name
      const currentIndex = shifts.findIndex((shift) => shift.name === current);

      // Calculate next index (cycle through shifts, "" means no shift)
      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = 0; // If empty or unknown, go to first shift
      } else if (currentIndex >= shifts.length - 1) {
        nextIndex = -1; // Cycle back to empty (denoted as "")
      } else {
        nextIndex = currentIndex + 1;
      }

      // Assign the next shift name or empty string if index is -1
      newSchedule[pIndex][dayIndex] =
        nextIndex === -1 ? "" : shifts[nextIndex].name;

      return newSchedule;
    });
  };
  const openEditParticipant = (index) => {
    const p = participants[index];
    setEditData({
      name: p.name,
      maxShiftsPerPerson: p.maxShiftsPerPerson,
      minEmptyDaysBetweenShifts: p.minEmptyDaysBetweenShifts,
    });
    setEditIndex(index);
  };

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = () => {
    if (
      !editData.name.trim() ||
      editData.maxShiftsPerPerson < 0 ||
      editData.minEmptyDaysBetweenShifts < 0
    ) {
      alert(t("Please enter valid values"));
      return;
    }
    setParticipants((prev) => {
      const updated = [...prev];
      updated[editIndex] = { ...editData };
      return updated;
    });
  };

  const goNext = () => {
    saveEdit(); // Save current before moving on
    setEditIndex((prev) => (prev + 1) % participants.length);
    const nextP = participants[(editIndex + 1) % participants.length];
    setEditData({ ...nextP });
  };

  const goPrev = () => {
    saveEdit();
    setEditIndex(
      (prev) => (prev - 1 + participants.length) % participants.length
    );
    const prevP =
      participants[(editIndex - 1 + participants.length) % participants.length];
    setEditData({ ...prevP });
  };
  const toggleHoliday = (dayIndex) => {
    setHolidays((prev) => {
      if (prev.includes(dayIndex)) {
        return prev.filter((d) => d !== dayIndex);
      } else {
        return [...prev, dayIndex];
      }
    });
  };

  const totalHolidayShifts = participants.reduce((acc, _, pIndex) => {
    return (
      acc +
      Object.entries(schedule[pIndex]).filter(
        ([dayIndex, val]) =>
          val !== "" && val !== "-" && holidays.includes(Number(dayIndex))
      ).length
    );
  }, 0);

  // --- New state for title and dialog ---
  const [title, setTitle] = useState(
    defaults.title !== undefined ? defaults.title : "New Schedule"
  );
  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);
  const handleOpenTitleDialog = () => {
    setIsTitleDialogOpen(true);
  };

  const handleCloseTitleDialog = () => {
    setIsTitleDialogOpen(false);
  };

  const handleSaveTitle = (newTitle) => {
    if (newTitle && newTitle.trim() !== "") {
      setTitle(newTitle.trim());
    }
    handleCloseTitleDialog();
  };

  const handleExport = () => {
    exportToExcel({ participants, schedule, holidays, days, t });
  };
  // --- New state for participant history dialog ---
  const [isParticipantHistoryOpen, setIsParticipantHistoryOpen] =
    useState(false);

  // Extract unique participants from user's schedule history (excluding defaults)
  const participantHistory = useMemo(() => {
    const allParticipants = [];

    userSchedules.forEach((schedule) => {
      if (schedule.participants) {
        schedule.participants.forEach((participant) => {
          // Skip default participants (those with "Άτομο" or "Person" in their names)
          if (
            participant.name.includes("Άτομο") ||
            participant.name.includes("Person")
          ) {
            return;
          }

          // Only add if we don't already have a participant with this exact name and settings
          const exists = allParticipants.some(
            (existing) =>
              existing.name === participant.name &&
              existing.maxShiftsPerPerson === participant.maxShiftsPerPerson &&
              existing.minEmptyDaysBetweenShifts ===
                participant.minEmptyDaysBetweenShifts
          );

          if (!exists) {
            allParticipants.push({
              name: participant.name,
              maxShiftsPerPerson: participant.maxShiftsPerPerson,
              minEmptyDaysBetweenShifts: participant.minEmptyDaysBetweenShifts,
            });
          }
        });
      }
    });

    // Sort by name for consistent ordering
    return allParticipants.sort((a, b) => a.name.localeCompare(b.name));
  }, [userSchedules]);

  const handleOpenParticipantHistory = () => {
    setIsParticipantHistoryOpen(true);
  };

  const handleCloseParticipantHistory = () => {
    setIsParticipantHistoryOpen(false);
  };

  const handleAddParticipantsFromHistory = (selectionData) => {
    const { toAdd, toReplace } = selectionData;

    setParticipants((prev) => {
      // Remove participants that were selected for replacement
      let newParticipants = prev.filter(
        (participant) =>
          !toReplace.some(
            (replaceParticipant) => replaceParticipant.name === participant.name
          )
      );

      // Add new participants from history
      newParticipants = [...newParticipants, ...toAdd];

      return newParticipants;
    });

    // Update the schedule to match the new participant structure
    setSchedule((prev) => {
      const newSchedule = {};

      // Get indices of participants that are being replaced
      const replaceIndices = [];
      participants.forEach((participant, index) => {
        if (
          toReplace.some(
            (replaceParticipant) => replaceParticipant.name === participant.name
          )
        ) {
          replaceIndices.push(index);
        }
      });

      // Create schedule entries for all new participants
      let newIndex = 0;

      // First, copy schedules for participants that are staying
      participants.forEach((participant, oldIndex) => {
        if (
          !toReplace.some(
            (replaceParticipant) => replaceParticipant.name === participant.name
          )
        ) {
          newSchedule[newIndex] = {};

          // Copy existing schedule for this participant
          for (let dayIndex = 0; dayIndex < days; dayIndex++) {
            newSchedule[newIndex][dayIndex] = prev[oldIndex]
              ? prev[oldIndex][dayIndex] || ""
              : "";
          }

          newIndex++;
        }
      });

      // Then, add empty schedules for new participants
      toAdd.forEach(() => {
        newSchedule[newIndex] = {};

        // Initialize all days for the new participant
        for (let dayIndex = 0; dayIndex < days; dayIndex++) {
          newSchedule[newIndex][dayIndex] = "";
        }

        newIndex++;
      });

      return newSchedule;
    });
  };

  const handleReorderParticipants = (fromIndex, toIndex) => {
    // Create new arrays for participants and schedule
    const newParticipants = [...participants];
    const newSchedule = { ...schedule };

    // Remove the participant and their schedule from the original position
    const [movedParticipant] = newParticipants.splice(fromIndex, 1);
    const movedScheduleData = newSchedule[fromIndex];

    // Insert participant at the new position
    newParticipants.splice(toIndex, 0, movedParticipant);

    // Rebuild the schedule object with new indices
    const reorderedSchedule = {};
    let targetIndex = 0;

    // First, copy all schedules except the one being moved
    for (let i = 0; i < participants.length; i++) {
      if (i !== fromIndex) {
        if (targetIndex === toIndex) {
          // Insert the moved schedule at the target position
          reorderedSchedule[targetIndex] = movedScheduleData;
          targetIndex++;
        }
        reorderedSchedule[targetIndex] = newSchedule[i];
        targetIndex++;
      }
    }

    // If we haven't inserted the moved schedule yet (toIndex is at the end)
    if (targetIndex === toIndex) {
      reorderedSchedule[targetIndex] = movedScheduleData;
    }

    // Update the state
    setParticipants(newParticipants);
    setSchedule(reorderedSchedule);
  };

  // Return all necessary state and functions
  return {
    participants,
    setParticipants,
    schedule,
    setSchedule,
    holidays,
    setHolidays,
    handleAutocomplete,
    handleClear,
    handleClearAll,
    toggleUnavailableCell,
    handleCellRightClick,
    toggleHoliday,
    handleMouseDownLeft,
    handleMouseEnterLeft,
    handleMouseUpLeft,
    totalHolidayShifts,
    editingSchedule,
    isEditing,
    people,
    days,
    maxShiftsPerDay,
    maxShiftsPerPerson,
    minEmptyDaysBetweenShifts,
    shifts,
    editIndex,
    setEditIndex,
    editData,
    setEditData,
    openEditParticipant,
    handleEditChange,
    saveEdit,
    goPrev,
    goNext,
    title,
    handleSaveTitle,
    handleOpenTitleDialog,
    handleCloseTitleDialog,
    isTitleDialogOpen,
    handleExport,
    // New participant history functionality
    participantHistory,
    isParticipantHistoryOpen,
    handleOpenParticipantHistory,
    handleCloseParticipantHistory,
    handleAddParticipantsFromHistory,
    handleReorderParticipants,
  };
};

export default useScheduleLogic;
